import {
  Dispatch, SetStateAction, useEffect, useRef, useState,
} from 'react';
import { Lock, VisitInterface } from '../types';
import { CarryForwardType } from '../enums';
import {
  currentUserOwnsLock, fetchOrAcquireLock, getVisitIdForLock, isLockedByAnotherUser, updateProcedureLockStatus,
} from '../util/lockingUtils';
import { deleteLock } from '../api/esourceService';

interface UseProcedureLockParams {
  procedureId: string;
  procedureReadOnly: boolean;
  studyId: string;
  subjectId?: string;
  visitId?: string;
  carryForwardType?: CarryForwardType;
  isLoading: boolean;
  setVisitConfig: Dispatch<SetStateAction<VisitInterface | undefined>>;
}

interface UseProcedureLockResult {
  lockHolder: string;
  fetchInitialLock: () => Promise<Lock>;
  handleLockResult: (lock: Lock, visitConfig?: VisitInterface) => void;
  releaseLock: () => void;
}

/**
 * Custom hook to manage procedure locking logic
 * assumes mounting/ unmounting when procedureId changes, so only one procedure lock is managed at a time
 * @param studyId - used for lock path
 * @param subjectId - used for lock path
 * @param visitId - used for lock path, but may be overridden by carryForwardType logic
 * @param procedureId - used for lock path
 * @param carryForwardType - used to determine lock path based on if this is a permanent procedure
 * @param procedureReadOnly - if true, lock will not be acquired
 * @param isLoading - to determine when to activate visibility change listeners
 * @param setVisitConfig - to update readonly status in visitConfig after lock fetch
 */
export default function useProcedureLock({
  studyId,
  subjectId,
  visitId,
  procedureId,
  carryForwardType,
  procedureReadOnly,
  isLoading,
  setVisitConfig,
}: UseProcedureLockParams): UseProcedureLockResult {
  const mountedRef = useRef(true);
  const userLockRef = useRef<Lock | undefined>(undefined);
  const [lockHolder, setLockHolder] = useState('');

  // Refs for stable cleanup access (avoids stale closures in effect cleanups)
  const lockParams = {
    procedureId,
    studyId,
    subjectId,
    visitId: getVisitIdForLock(carryForwardType, visitId),
  };
  /**
   * Release the lock currently held by this user. Uses refs so it's safe
   * to call from any closure (effect cleanup, event handler, etc.).
   */
  const releaseLock = (): void => {
    if (procedureReadOnly || !currentUserOwnsLock(userLockRef.current)) {
      return;
    }
    deleteLock(lockParams);
    userLockRef.current = undefined;
    if (mountedRef.current) {
      setLockHolder('');
    }
  };

  /**
   * Acquire the lock. Stores lock in ref immediately so cleanup can
   * always find and release it, even if the component unmounts during the fetch.
   */
  const fetchLock = async (): Promise<Lock> => {
    const lock = await fetchOrAcquireLock({
      ...lockParams,
      procedureReadOnly,
    });

    // Store in ref immediately for cleanup tracking
    userLockRef.current = lock;

    // If unmounted while acquiring, release immediately
    if (!mountedRef.current) {
      releaseLock();
    }

    return lock;
  };

  /**
   * Process the lock result after initial load completes or after lock refetch.
   * Mutates visitConfig via updateProcedureLockStatus.
   */
  const handleLockResult = (lock: Lock, fetchedVisitConfig?: VisitInterface): void => {
    setVisitConfig((prev) => {
      const visitConfig = fetchedVisitConfig ?? prev;
      if (visitConfig === undefined) return visitConfig;
      // mutates visitConfig.procedures
      updateProcedureLockStatus(lock, visitConfig, procedureId);
      return structuredClone(visitConfig);
    });
    setLockHolder(isLockedByAnotherUser(lock) ? lock?.userName ?? '' : '');
  };

  // Handle visibility change for lock management - only active after loading completes
  // Known bug: if user tabs out before initial load completes, lock may not be released until they return and trigger the effect.
  // This is a tradeoff to avoid adding visibility listeners during loading when we know we don't have a lock yet.
  useEffect(() => {
    if (isLoading) return undefined;

    const reacquireLock = async (): Promise<void> => {
      const newLock = await fetchLock();
      if (mountedRef.current) {
        handleLockResult(newLock);
      }
    };

    const handleVisibilityChange = (): void => {
      if (document.hidden) {
        releaseLock();
      } else {
        reacquireLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLoading]);

  // Release lock on unmount
  // set mounted to false so we can release immediately if POST lock is still in-flight
  useEffect(() => () => {
    mountedRef.current = false;
    releaseLock();
  }, []);

  return {
    lockHolder,
    fetchInitialLock: fetchLock,
    handleLockResult,
    releaseLock,
  };
}
